import React, { useState, useEffect } from 'react';
import spotifyArtistsAgent from '../services/spotifyArtistsAgent';

const SpotifyArtistsConfig = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [instructions, setInstructions] = useState([]);
  const [authCheckInterval, setAuthCheckInterval] = useState(null);
  const [currentStatus, setCurrentStatus] = useState({
    isInitialized: false,
    isAuthenticated: false,
    needsVerification: false,
    sessionValid: false,
    lastActivity: null
  });

  useEffect(() => {
    return () => {
      // Cleanup interval on unmount
      if (authCheckInterval) {
        clearInterval(authCheckInterval);
      }
    };
  }, [authCheckInterval]);

  // Update current status periodically
  useEffect(() => {
    const updateStatus = async () => {
      if (sessionId) {
        try {
          const backendStatus = await spotifyArtistsAgent.getBackendStatus();
          console.log('🔧 Backend status received:', backendStatus);
          setCurrentStatus(backendStatus);
        } catch (error) {
          console.error('Failed to get status:', error);
          // Fallback to local status if backend fails
          setCurrentStatus(spotifyArtistsAgent.getStatus());
        }
      } else {
        // If no sessionId but we might have an active session, try to restore it
        try {
          // Check if there's an active session by trying to get the agent's current sessionId
          const agentSessionId = spotifyArtistsAgent.sessionId;
          if (agentSessionId) {
            console.log('🔧 Restoring lost sessionId:', agentSessionId);
            setSessionId(agentSessionId);
            const backendStatus = await spotifyArtistsAgent.getBackendStatus();
            console.log('🔧 Backend status after restore:', backendStatus);
            setCurrentStatus(backendStatus);
          } else {
            setCurrentStatus(spotifyArtistsAgent.getStatus());
          }
        } catch (error) {
          console.error('Failed to restore session:', error);
          setCurrentStatus(spotifyArtistsAgent.getStatus());
        }
      }
    };

    updateStatus();
    
    // Update status every 5 seconds if there's a session
    const statusInterval = setInterval(updateStatus, 5000);
    
    return () => clearInterval(statusInterval);
  }, [sessionId]);

  // Debug log for currentStatus changes
  useEffect(() => {
    console.log('🔧 currentStatus changed:', currentStatus);
    console.log('🔧 Should show Extract button?', currentStatus.isAuthenticated && sessionId);
  }, [currentStatus, sessionId]);

  const handleInitializeAgent = async () => {
    setIsLoading(true);
    try {
      console.log('🤖 Initializing Spotify Artists Agent...');
      
      const response = await spotifyArtistsAgent.initialize({});
      
      if (response.success) {
        setSessionId(response.sessionId);
        setInstructions(response.instructions || []);
        
        console.log('✅ Agent initialized successfully');
        console.log('📋 Instructions:', response.instructions);
        
        // Start checking authentication status
        startAuthenticationCheck(response.sessionId);
        
        setStatus({
          type: 'success',
          message: response.message
        });
      } else {
        setStatus({
          type: 'error',
          message: response.message || 'Failed to initialize agent'
        });
      }
    } catch (error) {
      console.error('❌ Agent initialization failed:', error);
      setStatus({
        type: 'error',
        message: error.message || 'Failed to initialize agent'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const startAuthenticationCheck = (sessionId) => {
    console.log('🔍 Starting authentication status checks...');
    
    const interval = setInterval(async () => {
      try {
        const agentStatus = await spotifyArtistsAgent.getBackendStatus();
        
        if (agentStatus.isAuthenticated) {
          console.log('✅ User successfully authenticated!');
          clearInterval(interval);
          setAuthCheckInterval(null);
          
          setStatus({
            type: 'success',
            message: '🎉 Successfully authenticated! You can now extract playlist data.'
          });
        } else {
          console.log('⏳ Waiting for user authentication...');
        }
      } catch (error) {
        console.warn('Failed to check auth status:', error);
      }
    }, 3000); // Check every 3 seconds
    
    setAuthCheckInterval(interval);
  };

  const handleCheckAuthStatus = async () => {
    if (!sessionId) {
      alert('Please initialize the agent first');
      return;
    }

    try {
      const agentStatus = await spotifyArtistsAgent.getBackendStatus();
      
      if (agentStatus.isAuthenticated) {
        setStatus({
          type: 'success',
          message: '✅ Authenticated and ready to extract playlist data!'
        });
        
        // Stop auto-checking if manually verified
        if (authCheckInterval) {
          clearInterval(authCheckInterval);
          setAuthCheckInterval(null);
        }
      } else {
        setStatus({
          type: 'warning',
          message: '⏳ Not yet authenticated. Please complete login in the browser window.'
        });
      }
    } catch (error) {
      console.error('Failed to check authentication status:', error);
      setStatus({
        type: 'error',
        message: 'Failed to check authentication status'
      });
    }
  };

  const handleCleanup = async () => {
    try {
      await spotifyArtistsAgent.cleanup();
      setSessionId(null);
      setStatus(null);
      setInstructions([]);
      
      if (authCheckInterval) {
        clearInterval(authCheckInterval);
        setAuthCheckInterval(null);
      }
      
      console.log('🧹 Agent cleaned up successfully');
    } catch (error) {
      console.error('Failed to cleanup agent:', error);
    }
  };

  const handleExtractDashboard = async () => {
    if (!sessionId) {
      alert('Please initialize the agent first');
      return;
    }

    if (!currentStatus.isAuthenticated) {
      alert('Please authenticate first by logging into Spotify for Artists');
      return;
    }

    setIsLoading(true);
    try {
      console.log('🔍 Starting dashboard data extraction...');
      
      setStatus({
        type: 'success',
        message: '🔍 Intelligently exploring your Spotify for Artists dashboard... The agent will click through all available navigation and extract data from every accessible page. This will take 5-10 minutes as it discovers and analyzes your complete dashboard.'
      });

      const dashboardData = await spotifyArtistsAgent.extractDashboardData();
      
      console.log('✅ Dashboard data extracted:', dashboardData);
      
      // Count successfully extracted pages
      const successfulExtractions = Object.entries(dashboardData)
        .filter(([key, value]) => key !== 'extractedAt' && value && !value.error)
        .length;
      
      const totalPages = Object.keys(dashboardData).length - 1; // Exclude extractedAt
      
      setStatus({
        type: 'success',
        message: `🎉 Successfully explored and extracted data from ${successfulExtractions} dashboard sections! 

🧭 INTELLIGENT NAVIGATION RESULTS:
${dashboardData.overview && !dashboardData.overview.error ? '✅ Main Dashboard Overview' : '❌ Overview not accessible'}
${dashboardData.music && !dashboardData.music.error ? '✅ Music & Catalog Section' : '❌ Music section not found'}
${dashboardData.audience && !dashboardData.audience.error ? '✅ Audience Insights' : '❌ Audience section not found'}
${dashboardData.analytics && !dashboardData.analytics.error ? '✅ Analytics Section' : '❌ Analytics not accessible'}
${dashboardData.profile && !dashboardData.profile.error ? '✅ Profile Section' : '❌ Profile not accessible'}

Plus any additional sections discovered by navigation!

💾 DATA STORAGE: ${dashboardData.storage?.saved ? '✅ Saved to database' : '❌ Storage failed'}
🔗 AVAILABLE FOR: All DIZEE apps (Playlisting, Analytics, Insights, Marketing)
📊 CHECK CONSOLE: For detailed navigation and extraction logs

Your complete dashboard intelligence is now available across all apps!`
      });

      // Display key insights if available
      if (dashboardData.music && dashboardData.music.playlists) {
        console.log('🎵 Playlist placements found:', dashboardData.music.playlists);
      }
      
      if (dashboardData.overview && dashboardData.overview.tracks) {
        console.log('📊 Track performance data:', dashboardData.overview.tracks);
      }
      
    } catch (error) {
      console.error('❌ Dashboard extraction failed:', error);
      setStatus({
        type: 'error',
        message: `Failed to extract dashboard data: ${error.message}. Make sure you're logged into Spotify for Artists and try again.`
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-4">
      <div className="mb-4">
        <h3 className="text-white text-[14px] font-semibold mb-2">
          🤖 Spotify for Artists Agent
        </h3>
        <p className="text-[rgba(255,255,255,0.7)] text-[10px]">
          Automatically extract playlist placement data from your Spotify for Artists dashboard using AI vision.
        </p>
      </div>

      <div className="space-y-3">
        {/* Instructions */}
        {instructions.length > 0 && (
          <div className="bg-[rgba(88,86,214,0.1)] border border-[rgba(88,86,214,0.3)] rounded-md p-3">
            <h4 className="font-medium text-[rgba(255,255,255,0.9)] text-[11px] mb-2">📋 Next Steps:</h4>
            <ol className="list-decimal list-inside space-y-1 text-[9px] text-[rgba(255,255,255,0.8)]">
              {instructions.map((instruction, index) => (
                <li key={index}>{instruction}</li>
              ))}
            </ol>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleInitializeAgent}
            disabled={isLoading || currentStatus.isInitialized}
            className="px-3 py-2 bg-[#5856D6] text-white rounded text-[10px] hover:bg-[#7775D8] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
          >
            {isLoading ? '🔄 Opening Browser...' : '🚀 Initialize Agent'}
          </button>

          {/* Show restore button if authenticated but no sessionId */}
          {!sessionId && currentStatus.isAuthenticated && (
            <button
              onClick={async () => {
                const agentSessionId = spotifyArtistsAgent.sessionId;
                if (agentSessionId) {
                  console.log('🔧 Manually restoring sessionId:', agentSessionId);
                  setSessionId(agentSessionId);
                } else {
                  alert('No active session found to restore');
                }
              }}
              className="px-3 py-2 bg-yellow-600 text-white rounded text-[10px] hover:bg-yellow-700 font-medium transition-colors"
            >
              🔧 Restore Session
            </button>
          )}

          {sessionId && (
            <>
              <button
                onClick={handleCheckAuthStatus}
                className="px-3 py-2 bg-green-600 text-white rounded text-[10px] hover:bg-green-700 font-medium transition-colors"
              >
                🔍 Check Auth Status
              </button>
              
              {currentStatus.isAuthenticated && (
                <button
                  onClick={handleExtractDashboard}
                  disabled={isLoading}
                  className="px-3 py-2 bg-orange-600 text-white rounded text-[10px] hover:bg-orange-700 disabled:opacity-50 font-medium transition-colors"
                >
                  {isLoading ? '🔄 Exploring Dashboard...' : '🧭 Intelligently Extract All Data'}
                </button>
              )}
              
              <button
                onClick={handleCleanup}
                className="px-3 py-2 bg-red-600 text-white rounded text-[10px] hover:bg-red-700 font-medium transition-colors"
              >
                🧹 Cleanup & Close
              </button>
            </>
          )}
        </div>

        {/* Status Display */}
        {status && (
          <div className={`p-3 rounded-md border ${
            status.type === 'success' ? 'bg-[rgba(34,197,94,0.1)] border-[rgba(34,197,94,0.3)]' :
            status.type === 'warning' ? 'bg-[rgba(234,179,8,0.1)] border-[rgba(234,179,8,0.3)]' :
            'bg-[rgba(239,68,68,0.1)] border-[rgba(239,68,68,0.3)]'
          }`}>
            <p className={`text-[10px] ${
              status.type === 'success' ? 'text-green-400' :
              status.type === 'warning' ? 'text-yellow-400' :
              'text-red-400'
            }`}>
              {status.message}
            </p>
          </div>
        )}

        {/* Agent Status */}
        <div className="bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-md p-3">
          <h4 className="font-medium text-[rgba(255,255,255,0.9)] text-[11px] mb-2">Agent Status:</h4>
          <div className="text-[9px] text-[rgba(255,255,255,0.7)] space-y-1">
            <div className="flex items-center gap-2">
              <span>🔧 Initialized:</span>
              <span className={currentStatus.isInitialized ? 'text-green-400' : 'text-red-400'}>
                {currentStatus.isInitialized ? '✅ Yes' : '❌ No'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span>🔐 Authenticated:</span>
              <span className={currentStatus.isAuthenticated ? 'text-green-400' : 'text-red-400'}>
                {currentStatus.isAuthenticated ? '✅ Yes' : '❌ No'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span>⏰ Session Valid:</span>
              <span className={currentStatus.sessionValid ? 'text-green-400' : 'text-red-400'}>
                {currentStatus.sessionValid ? '✅ Yes' : '❌ No'}
              </span>
            </div>
            {authCheckInterval && (
              <div className="text-[#5856D6] text-[9px] mt-2">
                🔄 Auto-checking authentication every 3 seconds...
              </div>
            )}
          </div>
        </div>

        {/* Temporary Debug Info */}
        <div className="bg-[rgba(255,255,0,0.1)] border border-[rgba(255,255,0,0.3)] rounded-md p-3">
          <h4 className="font-medium text-yellow-400 text-[11px] mb-2">🔧 Debug Info (Temporary):</h4>
          <div className="text-[9px] text-[rgba(255,255,255,0.7)] space-y-1">
            <div>SessionId: {sessionId ? '✅ Present' : '❌ Missing'}</div>
            <div>IsAuthenticated: {currentStatus.isAuthenticated ? '✅ True' : '❌ False'}</div>
            <div>Show Extract Button: {(currentStatus.isAuthenticated && sessionId) ? '✅ YES' : '❌ NO'}</div>
            <div>Current Status Object: {JSON.stringify(currentStatus)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpotifyArtistsConfig; 